import { useEffect, useId, useMemo, useRef, useState } from "react";

export type Option = { value: string; label?: string; group?: string };

/** A compact multi-select: a button with the count, a searchable checkbox list. */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  searchable,
  name = label,
  className = "input",
  align = "left",
}: {
  /** The button text. */
  label: string;
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
  searchable?: boolean;
  /** What the list holds, for its search box and clear link; defaults to the label. */
  name?: string;
  className?: string;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (t ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(t)) : options).slice(0, 300);
  }, [q, options]);
  const groups = [...new Set(shown.map((o) => o.group ?? ""))];
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={`${className} flex items-center gap-1.5 whitespace-nowrap ${value.length ? "border-brand-2 text-brand" : ""}`}
      >
        {label}
        {value.length > 0 && <span className="tnum rounded-badge bg-brand-2 text-white text-[11px] px-1.5">{value.length}</span>}
        <span aria-hidden className="text-muted">▾</span>
      </button>
      {open && (
        <div id={id} className={`card absolute z-30 mt-1 w-72 max-w-[90vw] shadow-lg p-2 ${align === "right" ? "right-0" : ""}`}>
          {(searchable || options.length > 12) && (
            <input className="input w-full mb-2" placeholder={`Search ${name.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          )}
          <div role="listbox" aria-multiselectable className="max-h-72 overflow-y-auto">
            {groups.map((g) => (
              <div key={g}>
                {g && <div className="label px-1 pt-2 pb-1">{g}</div>}
                {shown
                  .filter((o) => (o.group ?? "") === g)
                  .map((o) => (
                    <label key={o.value} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-hover cursor-pointer text-[13px]">
                      <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
                      <span className="truncate">{o.label ?? o.value}</span>
                    </label>
                  ))}
              </div>
            ))}
            {!shown.length && <div className="text-muted text-[13px] p-2">No matches</div>}
          </div>
          {value.length > 0 && (
            <button className="text-[12px] text-brand mt-2" onClick={() => onChange([])}>
              Clear {name.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
